import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_volley-club/artifacts/dg49xs0l_Black%20Decorative%20Shield%20Volleyball%20Club%20Logo%20%28Sweatshirts%29%20%28T-Shirts%29.png";
const BACKEND_URL = window.location.origin;

const AboutPageNew = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Scroll animation observer
  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    if (!content) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-animate').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [content]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/about/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      // Use default content on error
      setContent({
        hero_title: "Welcome to NLV",
        hero_subtitle: "Amsterdam's most dynamic volleyball community",
        who_we_are_text: "We're not just a volleyball group — we're a family. Every week, volleyball lovers come together to play, connect, and have fun right here in Amsterdam. We welcome players who share the same love for the game and good vibes. 🧡",
        who_we_are_images: [],
        story_text: "From one small meetup to one of Amsterdam's biggest volleyball communities — our story is built on energy, friendship, and passion for the game.",
        timeline: [
          { year: "2022", event: "Our first friendly game in Amsterdam" },
          { year: "2023", event: "We reached 100 active members. We hosted first Halloween Party!" },
          { year: "2024", event: "NLV Spike-tecular Stars. We awarded our best players! 👑🏐" },
          { year: "2025", event: "Hosted more than 150 games so far. 🚀" },
          { year: "2026", event: "Stay Tuned!" }
        ],
        testimonials: [
          { quote: "Every week feels like a mini-festival!", author: "Lara", image: "smile_team.jpg" },
          { quote: "I joined alone — now I've got a whole new group of friends.", author: "Ozi", image: "serve_action.jpg" },
          { quote: "The energy, the people, the fun… this is my happy place.", author: "Burak", image: "aftergame_drinks.jpg" }
        ],
        values: [
          { icon: "🏐", title: "Passion for Play", desc: "We play hard, laugh harder." },
          { icon: "🤗", title: "Inclusivity", desc: "Everyone's welcome, always." },
          { icon: "🌍", title: "Diversity", desc: "Players from over 50 countries." },
          { icon: "💪", title: "Growth", desc: "We learn, train, and evolve together." },
          { icon: "🎉", title: "Fun", desc: "We never forget why we started: Joy." }
        ],
        gallery_images: [],
        contact_email: "nlvolleyballgroup@gmail.com",
        contact_instagram: "@nl_volleyball",
        contact_instagram_url: "https://www.instagram.com/nl_volleyball"
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToJoin = () => {
    document.getElementById('join-section').scrollIntoView({ behavior: 'smooth' });
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar user={user} onLogout={onLogout} />

      {/* Section 1: Hero */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden bg-black">
        {/* Video Background - Pure black like landing page */}
        <div className="absolute inset-0 bg-black">
          {/* Replace with actual video: <video autoPlay loop muted className="w-full h-full object-cover"><source src="/videos/volleyball-match-reel.mp4" type="video/mp4" /></video> */}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-6xl md:text-8xl font-bold mb-6 animate-fadeInUp">
            {content.hero_title.includes('NLV') ? (
              content.hero_title.split('NLV').map((part, i) => (
                <span key={i}>
                  {part}
                  {i < content.hero_title.split('NLV').length - 1 && <span className="text-orange-400">NLV</span>}
                </span>
              ))
            ) : (
              content.hero_title
            )}
          </h1>
          
          {/* Logo - No circle, just the logo on black background - 2x bigger */}
          <div className="flex justify-center mb-8 animate-fadeInUp animation-delay-200">
            <img 
              src={LOGO_URL} 
              alt="NL Volleyball Logo" 
              className="w-80 h-80 md:w-96 md:h-96 object-contain"
            />
          </div>
          
          <p className="text-2xl md:text-3xl mb-8 animate-fadeInUp animation-delay-400">
            {content.hero_subtitle}
          </p>
          <button
            onClick={scrollToJoin}
            className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-full text-xl font-bold shadow-2xl transform hover:scale-110 transition-all duration-300 animate-fadeInUp animation-delay-400 hover:shadow-orange-500/50"
          >
            Join Us ⚡
          </button>
        </div>

        {/* Scroll Indicator - Hidden on mobile */}
        <div className="hidden md:block absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Section 2: Who We Are */}
      <section className="py-20 px-6 md:px-12 scroll-animate opacity-0">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Photo Collage */}
            <div className="grid grid-cols-2 gap-4">
              {content.who_we_are_images && content.who_we_are_images.length > 0 ? (
                content.who_we_are_images.slice(0, 3).map((url, index) => (
                  <div 
                    key={index}
                    className={`rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-300 ${index === 2 ? 'col-span-2' : ''}`}
                  >
                    <img 
                      src={`${BACKEND_URL}${url}`} 
                      alt={`NLV Team ${index + 1}`}
                      className={`w-full object-cover ${index === 2 ? 'h-72' : 'h-64'}`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                ))
              ) : (
                <>
                  <div className="rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-300">
                    <div className="bg-gradient-to-br from-orange-300 to-orange-500 h-64 flex items-center justify-center text-white text-sm p-4 text-center">
                      📸 Admin can upload photos
                    </div>
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-300">
                    <div className="bg-gradient-to-br from-blue-300 to-blue-500 h-64 flex items-center justify-center text-white text-sm p-4 text-center">
                      📸 Admin can upload photos
                    </div>
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-300 col-span-2">
                    <div className="bg-gradient-to-br from-orange-400 to-pink-500 h-72 flex items-center justify-center text-white text-sm p-4 text-center">
                      📸 Admin can upload photos
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Text Block */}
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Who We <span className="text-orange-500">Are?</span>
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed whitespace-pre-line">
                {content.who_we_are_text}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Our Story */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-br from-blue-50 to-orange-50 scroll-animate opacity-0">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-orange-500">Story</span>
          </h2>
          <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto whitespace-pre-line">
            {content.story_text}
          </p>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline Line - Hidden on mobile */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-orange-500 to-blue-500"></div>

            {content.timeline?.map((item, index) => (
              <div
                key={index}
                className={`relative mb-8 md:mb-12 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}
              >
                <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}>
                  <div className="bg-white rounded-2xl shadow-xl p-6 transform hover:scale-105 transition-all duration-300">
                    <div className="text-3xl font-bold text-orange-500 mb-2">{item.year}</div>
                    <p className="text-lg text-gray-700">{item.event}</p>
                  </div>
                </div>

                {/* Timeline Dot - Hidden on mobile */}
                <div className="hidden md:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-orange-500 rounded-full border-4 border-white shadow-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Community Vibes */}
      <section className="py-20 px-6 md:px-12 scroll-animate opacity-0">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-12">
            Community <span className="text-orange-500">Vibes</span> 🧡
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {content.testimonials && content.testimonials.length > 0 ? (
              content.testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="group relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  {/* Image */}
                  {testimonial.image && testimonial.image.startsWith('/') ? (
                    <img src={`${BACKEND_URL}${testimonial.image}`} alt={testimonial.author} className="h-96 w-full object-cover" onError={(e) => { e.target.parentElement.innerHTML = '<div class="h-96 bg-gradient-to-br from-orange-300 via-pink-300 to-blue-300 flex items-center justify-center text-white text-sm">📸 Image not found</div>'; }} />
                  ) : (
                    <div className="h-96 bg-gradient-to-br from-orange-300 via-pink-300 to-blue-300 flex items-center justify-center text-white text-sm">
                      📸 {testimonial.image || 'Upload image'}
                    </div>
                  )}

                  {/* Hover Overlay with Quote */}
                  <div className="absolute inset-0 bg-black bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center p-6 text-white">
                    <p className="text-2xl font-bold mb-4 text-center">"{testimonial.quote}"</p>
                    <p className="text-lg text-orange-400">— {testimonial.author}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center text-gray-500 py-12">
                No testimonials yet. Admin can add them.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Section 5: Join the Game */}
      <section
        id="join-section"
        className="py-20 px-6 md:px-12 bg-gradient-to-br from-orange-500 to-orange-600 text-white scroll-animate opacity-0"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-12">
            Join the Fun! 🎉
          </h2>

          <div className="flex flex-wrap justify-center gap-6">
            <button
              onClick={() => navigate('/games')}
              className="bg-white text-orange-500 px-8 py-4 rounded-full text-xl font-bold shadow-2xl transform hover:scale-110 transition-all duration-300 hover:shadow-white/50"
            >
              ⚡ Join Our Next Game
            </button>
            <a
              href="https://www.instagram.com/nl_volleyball"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full text-xl font-bold shadow-2xl transform hover:scale-110 transition-all duration-300"
            >
              📸 Follow @nl_volleyball
            </a>
            <button
              onClick={() => navigate('/contact')}
              className="bg-blue-500 text-white px-8 py-4 rounded-full text-xl font-bold shadow-2xl transform hover:scale-110 transition-all duration-300 hover:shadow-blue-500/50"
            >
              💬 Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* Section 6: Our Values */}
      <section className="py-20 px-6 md:px-12 scroll-animate opacity-0">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-12">
            Our <span className="text-orange-500">Values</span>
          </h2>

          <div className="grid md:grid-cols-5 gap-8">
            {content.values?.map((value, index) => (
              <div
                key={index}
                className="text-center group transform hover:scale-110 transition-all duration-300"
              >
                <div className="text-6xl mb-4 group-hover:animate-bounce">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Gallery */}
      <section className="py-20 px-6 md:px-12 bg-gray-50 scroll-animate opacity-0">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-12">
            <span className="text-orange-500">Gallery</span> 📸
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {content.gallery_images && content.gallery_images.length > 0 ? (
              content.gallery_images.map((url, index) => (
                <div
                  key={index}
                  className="rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  {url.includes('.mp4') || url.includes('.webm') ? (
                    <video src={`${BACKEND_URL}${url}`} className="w-full h-64 object-cover" controls />
                  ) : (
                    <img src={`${BACKEND_URL}${url}`} alt={`Gallery ${index + 1}`} className="w-full h-64 object-cover" />
                  )}
                </div>
              ))
            ) : (
              [...Array(6)].map((_, num) => (
                <div
                  key={num}
                  className="rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  <div className="h-64 bg-gradient-to-br from-blue-300 via-purple-300 to-orange-300 flex items-center justify-center text-white text-lg font-bold">
                    📸 Replace: photo_{num + 1}.jpg
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Section 8: Stay Connected */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-br from-blue-500 to-blue-600 text-white scroll-animate opacity-0">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold mb-6">
            Stay <span className="text-orange-400">Connected</span> 💬
          </h2>
          <p className="text-2xl mb-12">
            We'd love to meet you!
          </p>

          <div className="space-y-4 text-xl">
            <p>📍 Based in Amsterdam</p>
            <p>📧 <a href={`mailto:${content.contact_email}`} className="underline hover:text-orange-400">{content.contact_email}</a></p>
            <p>
              📱 <a
                href={content.contact_instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-orange-400"
              >
                {content.contact_instagram} on Instagram
              </a>
            </p>
          </div>

          <div className="mt-12">
            <button
              onClick={() => navigate('/contact')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-full text-xl font-bold shadow-2xl transform hover:scale-110 transition-all duration-300"
            >
              Send Us a Message 📨
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPageNew;
